import { GridHeaderSelectionChangeEvent, GridPagerSettings } from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import GridWrapper from 'src/components/GridWrapper';
import { Product } from 'src/types/catalog';
import { useContextSelector } from 'use-context-selector';
import useSearchProductColumns from '../../assets/static/search_products/useSearchProductColumns';
import SearchProductInformationPanel from './SearchProductInformationPanel';
import SearchResultsToolbarActions from './SearchProductToolbarActions';
import { getEffectiveRefineSearch } from './searchProductContext.utils';
import { SearchProductDetailsContext } from './SearchProductDetailsContext';
import { SearchProductFiltersContext } from './SearchProductFiltersContext';
import { SearchProductQueryContext } from './SearchProductQueryContext';
import { SearchProductSelectionsContext } from './SearchProductSelectionsContext';

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'multiple',
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 25, 50, 100],
};

const defaultProps = {
  tableTextId: 'Products',
  DataItemKey: 'productKey',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable,
  pageable: gridPagerSettings,
} as const;

const SearchProductListView: FC = () => {
  const { t } = useTranslation();

  const dataState = useContextSelector(SearchProductFiltersContext, (state) => state.dataState);
  const onDataStateChange = useContextSelector(
    SearchProductSelectionsContext,
    (state) => state.onDataStateChange
  );
  const onSelectionChange = useContextSelector(
    SearchProductSelectionsContext,
    (state) => state.onSelectionChange
  );
  const onItemChange = useContextSelector(
    SearchProductSelectionsContext,
    (state) => state.onItemChange
  );
  const productSelections = useContextSelector(
    SearchProductSelectionsContext,
    (state) => state.productSelections
  );
  const setProductSelections = useContextSelector(
    SearchProductSelectionsContext,
    (state) => state.setProductSelections
  );
  const productsData = useContextSelector(SearchProductQueryContext, (state) => state.products);
  const productsTotal = useContextSelector(SearchProductQueryContext, (state) => state.productsTotal);
  const loadingProducts = useContextSelector(
    SearchProductQueryContext,
    (state) => state.loadingProducts
  );
  const isPrimarySearchPending = useContextSelector(
    SearchProductQueryContext,
    (state) => state.isPrimarySearchPending
  );
  const isUpdatingCart = useContextSelector(
    SearchProductSelectionsContext,
    (state) => state.isUpdatingCart
  );
  const isSearchProductsError = useContextSelector(
    SearchProductQueryContext,
    (state) => state.isSearchProductsError
  );
  const isProductPanelOpen = useContextSelector(
    SearchProductDetailsContext,
    (state) => state.isProductPanelOpen
  );
  const handleCloseProductPanel = useContextSelector(
    SearchProductDetailsContext,
    (state) => state.handleCloseProductPanel
  );
  const refineSearch = useContextSelector(SearchProductFiltersContext, (state) => state.refineSearch);

  const hasRefineSearchValue = Boolean(getEffectiveRefineSearch(refineSearch));

  const catalogData = useMemo(() => {
    if (!productsData.length) {
      return [];
    }

    return productsData.map((item: Product) => {
      const selection = productSelections[item.productKey] || { selected: false, qty: 0 };
      return {
        ...item,
        selected: selection.selected,
        qty: selection.qty,
      };
    });
  }, [productsData, productSelections]);

  const columns = useSearchProductColumns();

  const handleHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement = event?.syntheticEvent?.target as HTMLInputElement;
      const { checked } = checkboxElement;

      setProductSelections((prev) => {
        const updated = { ...prev };
        catalogData.forEach((item) => {
          const { productKey } = item as Product;
          const current = prev[productKey] || { selected: false, qty: 0 };
          updated[productKey] = {
            selected: checked,
            qty: checked && current.qty === 0 ? 1 : checked ? current.qty : 0,
          };
        });
        return updated;
      });
    },
    [catalogData, setProductSelections]
  );

  return (
    <main className="flex h-full min-h-0 flex-col px-4 pb-4">
      {isSearchProductsError ? (
        <div className="mb-2 shrink-0 text-sm text-red-600">{t('searchResultsLoadFailed')}</div>
      ) : null}
      {hasRefineSearchValue && (
        <div className="mb-2 shrink-0 truncate text-sm font-[600]">
          {t('Showing results for')} &quot;{refineSearch.trim()}&quot;
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-hidden">
        <GridWrapper
          id="searchResultListTable"
          className="product-list-view h-full min-h-0 [&_.k-grid-header_.k-cell-inner]:!text-[12px] [&_.k-grid-header_.k-cell-inner]:!font-medium [&_.k-grid-td]:!font-medium [&_.k-table-td]:!font-medium [&_.k-grid-toolbar>*]:!w-full [&_.k-pager_.k-link:focus-visible]:!outline [&_.k-pager_.k-link:focus-visible]:!outline-2 [&_.k-pager_.k-link:focus-visible]:!outline-offset-[-2px] [&_.k-pager_.k-link:focus-visible]:!outline-[#1a73e8] [&_.k-pager_button:focus-visible]:!outline [&_.k-pager_button:focus-visible]:!outline-2 [&_.k-pager_button:focus-visible]:!outline-offset-[-2px] [&_.k-pager_button:focus-visible]:!outline-[#1a73e8]"
          columns={columns}
          dataSource={catalogData}
          total={productsTotal}
          dataState={dataState}
          onDataStateChange={onDataStateChange}
          height="100%"
          gridToolbarChildren={<SearchResultsToolbarActions />}
          {...defaultProps}
          loading={loadingProducts || isPrimarySearchPending || isUpdatingCart}
          noRecordText={t('No items to display')}
          onHeaderSelectionChange={handleHeaderSelectionChange}
          onSelectionChange={onSelectionChange}
          onItemChange={onItemChange}
          tableTextId="Items"
        />
      </div>
      <SearchProductInformationPanel
        isOpen={isProductPanelOpen}
        onClose={handleCloseProductPanel}
      />
    </main>
  );
};

export default SearchProductListView;
